import { DOMEvents, DOMUtils } from '../../../../utils/dom'

import { IAdsConfiguration, ICommentAdConfiguration } from '../../../../domain/conf/ads'
import { Utils } from '../../../../utils/common'
import { logger } from '../../../../utils/common/logger'
import { Store } from '../../../../utils/storage/keys'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { IViafouraWidget, ViafouraWidget } from './widget'

interface IWindow extends Window {
    vf: any
    vfQ: any
}

type Widget = 'commenting' | 'carousel' | 'conversationStarter'

declare let window: IWindow

export class Viafoura {
    public adLight: boolean
    public commentWidget: IViafouraWidget
    public carouselWidget: IViafouraWidget
    public conversationStarterWidget: IViafouraWidget

    public displayAds = true
    public loadViafouraWidgets = false

    private DOM = {
        attrs: {
            jsUrl: '//cdn.viafoura.net/vf-v2.js',
            myTopics: 'data-my-topics',
            signOut: 'data-id-logout'
        },
        classes: {
            adLight: 'ad__adlight'
        },
        loginRadiusClasses: {
            emailValidation: 'email-validation-container'
        },
        loginRadiusIds: {
            forgotPassword: 'forgotpassword-container',
            login: 'login-container',
            registration: 'registration-container',
            resetPassword: 'resetpassword-container'
        },
        widgets: {
            contentRecirculation: 'vf-content-recirculation',
            conversationStarter: 'vf-conversation-starter',
            conversations: 'vf-conversations',
            dropdownItem: 'vf-dropdown__list-item',
            trayTrigger: 'vf-tray-trigger'
        },
        wrappers: {
            carousel: {
                selector: 'data-sso-carousel',
                title: 'Most Commented'
            },
            commenting: {
                attrs: {
                    commentsLimit: '',
                    commentsLoadMoreLimit: '',
                    firstPromoPosition: '1',
                    promoInterval: '10',
                    replyLimit: ''
                },
                customEventHandlers: {
                    click: (_widget: ViafouraWidget, event: Event) => {
                        const target = event.target as HTMLAnchorElement
                        /* istanbul ignore next */
                        if (!target.innerHTML.includes('Load more comments') ||
                         target.getAttribute('data-evt-skip-click')=== 'true') {
                            return
                        }

                        const occurrence = target.parentElement.classList.contains('vf-load-more') ||
                        target.parentElement.classList.contains('vf-loader-button__content')? 'other':'first'
                        /* istanbul ignore next */
                        const customEvent = new CustomEvent(
                            'custom-event',
                            {
                                detail: {
                                    action_type: 'load_more',
                                    context: 'comments',
                                    control_fields: {
                                        mparticle: {
                                            keys: {
                                                action_type: 'action_type',
                                                context: 'context',
                                                occurrence: 'occurrence'
                                            },
                                            mp_event_type: 'Other',
                                        },
                                    },
                                    event_name: 'on_page_action_click',
                                    occurrence
                                }
                            }
                        )
                        document.dispatchEvent(customEvent)
                    }
                },
                selector: 'data-sso-commenting',
                title: ''
            },
            conversationStarter: {
                customEventHandlers: {
                    click: (widget: ViafouraWidget, event: Event) => {
                        const target = event.target as HTMLAnchorElement
                        /* istanbul ignore next */
                        if (!target.classList.contains('vf-conversation-starter_link')) {
                            return
                        }
                        /* istanbul ignore next */
                        const customEvent = new CustomEvent(
                            'custom-event',
                            {
                                detail: {
                                    control_fields: {
                                        mparticle: {
                                            keys: {
                                                source_section: 'source_section',
                                                source_state: 'source_state',
                                                target_name: 'target_name'
                                            },
                                            mp_event_type: 'Other',
                                        },
                                    },
                                    event_name: 'click_to_section',
                                    source_section: 'conversation_starter',
                                    source_state: widget.getConversationStarterState(),
                                    target_name: new URL(target.href).hash,
                                }
                            }
                        )
                        document.dispatchEvent(customEvent)
                    }
                },
                description: 'Leave a comment and share your thoughts with our community.',
                selector: 'data-sso-conversation-starter',
                target: 'comments',
                title: 'Have your say',
            }
        }
    }

    private viafouraClickListeners: { [key: string]: (...args: any[]) => void } = {
        'trending-list__title': this.displayConsole,
        'vf-js-tray-activator': this.displayConsole,
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: social login provider name, or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            sign_in_method: 'sign_in_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signin,
                integration_name: 'Viafoura',
                sign_in_method: signInMethod
            }
        }))
    }

    /**
     * Send registration analytics event
     * @param {string} registrationMethod: registration provider name, or 'traditionalSignin'
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            registration_method: 'registration_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.registration,
                integration_name: 'Viafoura',
                registration_method: registrationMethod
            }
        }))
    }

    /**
     * get vf comment text from localstorage, from before redirect to sign-in page
     * if it exists, populate the textarea with the value
     */
    public static async PopulateCommentSection(): Promise<void> {
        const vfCommentText = localStorage.getItem(Store.VF_COMMENT_TEXT)
        if (vfCommentText) {
            logger('debug', 'populating viafoura comment textarea with: ' + vfCommentText)
            // tslint:disable-next-line: max-line-length
            const vfCommentTextArea = document.querySelector('.vf-content-editor-input') as HTMLTextAreaElement
            if (vfCommentTextArea) {
                vfCommentTextArea.value = vfCommentText
                logger('debug', 'populated vf comment textarea successfully')
            } else {
                logger('debug', 'unable to populate vf comment textarea, cannot find element')
            }
            // remove the value from localstorage
            localStorage.removeItem(Store.VF_COMMENT_TEXT)
        }
    }

    public static ScrollToComments() {
        // if query param "comments" exists in URL, scroll to section with id "comments"
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.has('comments')) {
            const commentsSection = document.getElementById('comments')
            if (commentsSection) {
                commentsSection.scrollIntoView()
            }
        }
    }

    public enable() {
        this.addSubscriptions()
        this.addListeners()
    }

    public addSubscriptions() {
        window.vfQ.push(() => {
            this.buildSubscriptions()
        })
    }

    public buildSubscriptions() {
        if (this.commentWidget && this.displayAds) {
            logger('debug', 'Viafoura $subscribe bind comment Ad Slot')
            // subscribe to the viafoura event fired when the ad slot is available in the comment widget
            window.vf.$subscribe(
                'vf-ads',
                'requestCommentAd',
                // bind the correct scope to handle the ad slot for the widget
                this.commentWidget.handleAdSlotEvent.bind(this.commentWidget)
            )
        }
        if (this.carouselWidget && this.displayAds) {
            logger('debug', 'Viafoura $subscribe bind carousel Ad Slot')
            // subscribe to the viafoura event fired when the ad slot is available in the carousel widget
            window.vf.$subscribe(
                'vf-ads',
                'requestContentRecirculationAd',
                // bind the correct scope to handle the ad slot for the widget
                this.carouselWidget.handleAdSlotEvent.bind(this.carouselWidget)
            )
        }

        window.vf.$subscribe('logout', 'success', () => {
            logger('debug', 'Viafoura $subscribe logout success')
            document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGOUT))
        })
        window.vf.$prepublish((channel: string, event: string, ...args: object[]) => {
            if (channel === 'authentication' && event === 'required') {
                logger('debug', 'Viafoura $prepublish authentication required')
                // get value of textarea with class vf-content-editor-input and store it in localstorage
                const textarea = document.querySelector<HTMLTextAreaElement>('.vf-content-editor-input')
                if (textarea) {
                    localStorage.setItem(Store.VF_COMMENT_TEXT, textarea.value)
                }
                const commentParam = location.search.length > 0 ? '&comments' : '?comments'
                const redirectUrl = location.href + commentParam
                document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGIN, {redirectUrl}))
                return false
            }

            return {channel, event, args}
        })

    }

    public addListeners() {
        document.addEventListener('click', async (e: Event) => {
            const el = e.target as Element
            for (const target in this.viafouraClickListeners) {
                if (el.classList.contains(target)) {
                    this.eventFunction(this.viafouraClickListeners[target], target)
                }
            }

        })
        const commentsSection = document.querySelector('.comments__section-viafoura-container') as HTMLElement
        if (commentsSection) {
            commentsSection.addEventListener('click', async () => {
                logger('debug', 'Viafoura clicked - populating comments section')
                await Viafoura.PopulateCommentSection()
            })
        }

    }

    /**
     * Remove ads from any registered widgets
     */
    public removeCommentWidgetsAdUI() {
        this.displayAds = false
        for (const widget of Object.values(this.DOM.wrappers)) {
            this.updateAdlightUserStatus(widget, this.displayAds)
            logger('debug', 'Viafoura set adLight status for: ' + widget)
        }
    }

    /**
     * Update adLight class to widget container if valid adLight user
     * @param widget
     * @param displayAds
     */
    public updateAdlightUserStatus(widget: any, displayAds: boolean) {
        const elArr = document.querySelectorAll<HTMLInputElement>(`[${widget.selector}]`)

        if (elArr && !displayAds) {
            for (const el of elArr) {
                DOMUtils.addClass(el, this.DOM.classes.adLight)
                logger('debug', `viafoura remove ads from: ${el}`)
            }
        } else if (elArr && displayAds) {
            for (const el of elArr) {
                DOMUtils.removeClass(el, this.DOM.classes.adLight)
            }
        }
    }

    public displayConsole(target: string) {
        logger('debug', 'target: ', target)
    }

    public eventFunction(event: any, target: string) {
        event(target)
    }

    /**
     * Initialize commenting features
     *
     * @param conf
     * @param accessToken  - normally empty. populated with Login Radius token if present
     * @returns
     */
    public async initialize(conf?: IAdsConfiguration, accessToken?: string) {
        if (!!window.vfQ) {
            return this
        }

        window.vf = window.vf || {events: {}}
        window.vfQ = window.vfQ || []

        let commentAdConfiguration: ICommentAdConfiguration

        if (conf) {
            commentAdConfiguration = conf.commentAdConfiguration
        }

        if (commentAdConfiguration) {
            this.DOM.wrappers.commenting.attrs.firstPromoPosition = commentAdConfiguration.firstPromoPosition ?
                commentAdConfiguration.firstPromoPosition : '1'
            this.DOM.wrappers.commenting.attrs.promoInterval = commentAdConfiguration.promoInterval ?
                commentAdConfiguration.promoInterval : '10'
        }

        logger('debug', 'comment section is now in view')
        await DOMUtils.ready // wait for DOM
        // load viafoura script in pages with sign out link
        // it should not load on my topics sign-in page to avoid redirect issue on safari
        const dataSignOut = document.querySelector(`[${this.DOM.attrs.signOut}]`)
        const dataMyTopics = document.querySelector(`[${this.DOM.attrs.myTopics}]`)
        if (dataSignOut && !dataMyTopics) {
            await this.scriptLoad(this.DOM.attrs.jsUrl)
            logger('debug', 'Viafoura JS is now ready')
        }

        await this.validateViafouraRequired(this.DOM.wrappers)
        // get widget load status
        if (this.loadViafouraWidgets) {
            // load viafoura widgets
            for (const [type, widget] of Object.entries(this.DOM.wrappers)) {
                this.updateAdlightUserStatus(widget, this.displayAds)
                await this.loadViafouraUI(type as Widget, widget)
                logger('debug', 'Viafoura UI is now ready for: ' + widget)
            }
            // load viafoura scss
            await this.scssLoad()
            const commentMeterCookie = (document.cookie.split(';').find((c) =>
            c.trim().startsWith(`${Store.COMMENT_METER}=`)) || '').trim().split('=').pop() || 'false'

            logger('debug', 'Viafoura CSS is now ready')
            window.vfQ.push(async () => {
                // get user info
                const user = await window.vf.context.get('user')
                logger('debug', `Viafoura user: ${user}`)

                // login to Viafoura if not logged in already (user.anonymous is 0, if logged in)
                if (accessToken && user.anonymous !== 0) {
                    logger('debug', `logging in viafoura user using token from FEM ${accessToken}`)
                    window.vf.session.login.loginRadius(accessToken)
                }

                if(commentMeterCookie === 'expired') {
                    window.vf.$subscribe('commenting', 'loaded', ()=> {
                        const loadButtonElements = document.getElementsByClassName('vf-load-more__button')
                        if (loadButtonElements.length > 0) {
                            const loadButtonElement = loadButtonElements[0] as HTMLButtonElement
                            // block analytic event
                            loadButtonElement.setAttribute('data-evt-skip-click','true')
                            loadButtonElement.click()
                        }
                    })
                    if(this.commentWidget && this.commentWidget.el) {
                        const el = this.commentWidget.el.getElementsByTagName('vf-conversations')
                        if( el && el.length > 0) {
                            el[0].setAttribute('limit','1')
                        }
                    }
                }

                this.enable()
                await DOMUtils.ready
                // temporary disabled auto scroll to comments
                // Viafoura.ScrollToComments()
            })
        }

        return this
    }

    /**
     * Signout viafoura if using login radius tray integration
     */
    public async doWidgetSignout() {
        if (window.vf && window.vf.session) {
            logger('info', 'signing out viafoura user')
            await window.vf.session.logout()
            logger('info', 'viafoura user signed out')
        }
    }

    /**
     * Validate a viafoura wrapper is present in DOM
     */
    public async validateViafouraRequired(wrappers: object) {
        // await this.hasSession() // wait for session
        // Cycle through supported wrapper object
        for (const attrs of Object.values(wrappers)) {
            // Search for supported wrapper selector in dom
            if (document.querySelector(`[${attrs.selector}]`)) {
                // If selector is found then viafoura is set to required
                this.loadViafouraWidgets = true
                return true
            }
        }
        return false
    }

    /**
     * Load viafoura css file from scssUrl
     */
    public async scssLoad(): Promise<boolean> {
        try {
            logger('debug', `viafoura loading css './styles/viafoura.scss'`)
            const scss = import('./styles/viafoura.scss')
            DOMUtils.addStyles((await scss).default)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load viafoura js file from jsUrl
     */
    public async scriptLoad(jsUrl: string): Promise<boolean> {
        try {
            logger('debug', `viafoura loading script ${this.DOM.attrs.jsUrl}`)
            DOMUtils.loadScript(jsUrl)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load the available widgets as objects.  The ViafouraWidget class takes
     * care of the interaction with the ad slot
     */
    public async loadViafouraUI(type: Widget, widget: any) {
        try {
            // Get and set the element by selector
            const el = document.querySelector<HTMLInputElement>(`[${widget.selector}]`)
            if (el) {
                switch (type) {
                    case 'commenting':
                        logger('debug', 'viafoura creating commenting widget')

                        // Get initial properties of wrapper and store in object
                        widget.attrs.replyLimit = el.dataset.replyLimit
                        widget.attrs.commentsLimit = el.dataset.commentsLimit
                        widget.attrs.commentsLoadMoreLimit = el.dataset.commentsLoadMoreLimit

                        // BT Issue -
                        // Create commenting widget
                        this.commentWidget = new ViafouraWidget(
                            this.DOM.wrappers.commenting.selector,
                            [
                                {
                                    key: 'first-promo-position',
                                    value: widget.attrs.firstPromoPosition
                                },
                                {
                                    key: 'promoInterval',
                                    value: widget.attrs.promoInterval
                                },
                                {
                                    key: 'limit',
                                    value: widget.attrs.commentsLimit
                                },
                                {
                                    key: 'pagination-limit',
                                    value: widget.attrs.commentsLoadMoreLimit
                                },
                                {
                                    key: 'reply-limit',
                                    value: widget.attrs.replyLimit
                                }
                            ],
                            this.DOM.widgets.conversations,
                            [`${this.DOM.widgets.trayTrigger}`],
                            2, // tslint:disable-line: Profile: no-magic-numbers
                            this.DOM.wrappers.commenting.customEventHandlers
                        )
                        return true

                    case 'carousel':
                        logger('debug', 'viafoura creating carousel widget')

                        // Create carousel widget
                        this.carouselWidget = new ViafouraWidget(
                            this.DOM.wrappers.carousel.selector,
                            [
                                {
                                    key: 'title',
                                    value: widget.title
                                }
                            ],
                            this.DOM.widgets.contentRecirculation
                        )
                        return true

                    case 'conversationStarter':
                        logger('debug', 'viafoura creating conversation starter widget')

                        // Create carousel widget
                        this.conversationStarterWidget = new ViafouraWidget(
                            this.DOM.wrappers.conversationStarter.selector,
                            [
                                {
                                    key: 'title',
                                    value: widget.title
                                },
                                {
                                    key: 'description',
                                    value: widget.description
                                },
                                {
                                    key: 'target',
                                    value: widget.target,
                                }
                            ],
                            this.DOM.widgets.conversationStarter,
                            undefined,
                            1,
                            this.DOM.wrappers.conversationStarter.customEventHandlers
                        )
                        return true
                    }
            }
            logger('debug', `viafoura widget selector not found: ${widget.selector}`)
            return false

        } catch (err) {
            logger('error', `viafoura create ${widget.selector} error: ${err}`)
            return false
        }
    }
}
